// Professor & Mentoria (0.65): dias de aula da turma, envio direto para o grupo da turma e cobranças extras.
import { Alert } from 'react-native';
import type { Reg } from '../store/types';
import type { Ctx } from './tipos';
import { dataBr, din, junta, num, op, temNum } from './util';
import { novoReg, salvarVarios } from '../store/modulos';
import { abrirGrupos } from '../components/Grupos';
import { enviar, grupoDoRef, membrosDe, recarregarGrupos, type DadosEnvio, type MeuGrupo, type TipoEnvio } from '../lib/grupos';
import { SEMANA_CURTA, addDays, norm, parseYmd, today } from '../lib/date';
import { t } from '../lib/i18n';

// ─── Dias de aula ───
const CHAVES = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];

/** Dias da semana (0 = domingo) em que a turma tem aula: o campo "Dias de aula" ou, sem ele, o texto de "Dias e horários" (ex.: "seg e qua, 07:30"). */
export function diasDaTurma(turma: Reg | null | undefined): number[] {
  if (!turma) return [];
  const marcados: string[] = Array.isArray(turma.c.dias) ? turma.c.dias : [];
  if (marcados.length) return [...new Set(marcados.map(d => CHAVES.indexOf(norm(String(d)).slice(0, 3))).filter(i => i >= 0))].sort();
  const txt = norm(String(turma.c.horario ?? ''));
  const achados = new Set<number>();
  for (const m of txt.matchAll(/\b(dom|seg|ter|qua|qui|sex|sab)/g)) achados.add(CHAVES.indexOf(m[1]));
  return [...achados].sort();
}
export const ehDiaDeAula = (dias: number[], data: string) => !dias.length || dias.includes(parseYmd(data).getDay());
/** Próximo (passo 1) ou anterior (passo -1) dia de aula a partir de "data" (sem contar a própria data). */
export function outroDiaDeAula(dias: number[], data: string, passo: 1 | -1) {
  let d = addDays(data, passo);
  if (!dias.length) return d;
  for (let i = 0; i < 7 && !dias.includes(parseYmd(d).getDay()); i++) d = addDays(d, passo);
  return d;
}
/** Hoje, se for dia de aula; senão o próximo dia de aula. */
export const proximoDiaDeAula = (turma: Reg | null | undefined, de = today()) => {
  const dias = diasDaTurma(turma);
  return ehDiaDeAula(dias, de) ? de : outroDiaDeAula(dias, de, 1);
};
export const nomesDosDias = (dias: number[]) => dias.map(d => t(SEMANA_CURTA[d])).join(', ');

// ─── Enviar para o grupo da turma ───
async function grupoDaTurma(turmaId: string): Promise<MeuGrupo | undefined> {
  await recarregarGrupos();
  return grupoDoRef(turmaId);
}
const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));

function semGrupo(turma: Reg, ctx: Ctx) {
  const nome = junta(turma.c.nome, turma.c.disciplina) || t('Turma');
  Alert.alert(t('A turma ainda não tem grupo'), t('Crie o grupo da turma e mande o convite aos alunos. Depois, o envio vai direto para eles.'), [
    { text: t('Agora não'), style: 'cancel' },
    { text: t('Criar o grupo da turma'), onPress: () => abrirGrupos({ aba: ctx.mod, novo: { nome, ref: turma.id } }) },
  ]);
}

/** Envia direto para o grupo da turma (todos do grupo), com a opção de revisar antes no formulário do grupo. */
export async function enviarParaTurma(ctx: Ctx, turmaId: string | null | undefined, envio: { titulo: string; tipo: TipoEnvio; dados: DadosEnvio }, depois?: () => void) {
  const turma = ctx.porId(turmaId);
  if (!turma) return;
  const g = await grupoDaTurma(turma.id);
  if (!g) return semGrupo(turma, ctx);
  Alert.alert(t('Enviar para a turma'), t('Enviar "{t}" para todos do grupo {g}?', { t: envio.titulo, g: g.nome }), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Revisar antes'), onPress: () => abrirGrupos({ aba: ctx.mod, ref: turma.id, envio }) },
    { text: t('Enviar'), onPress: async () => {
      try { await enviar(g.id, envio.tipo, envio.titulo, envio.dados, null); depois?.(); Alert.alert(t('Enviado'), t('Cada pessoa do grupo recebe e decide se aceita.')); }
      catch (e) { erro(e); }
    } },
  ]);
}

/** Material ou recado (tipo Mural = aviso) para a turma. Marca "Já enviado para a turma". */
export function enviarMaterial(ctx: Ctx, r: Reg, marcar: () => void) {
  const aviso = r.c.tipo === 'Mural';
  const titulo = String(r.c.titulo || op(r.c.tipo) || t('Material'));
  enviarParaTurma(ctx, r.pai, { titulo, tipo: aviso ? 'aviso' : 'material', dados: {
    texto: String(r.c.texto ?? '').trim() || undefined, link: String(r.c.link ?? '').trim() || undefined, emoji: aviso ? '📢' : '📚' } }, marcar);
}

/** Cada aluno recebe só a própria nota. Os alunos são ligados às pessoas do grupo pelo nome (do aluno ou do responsável). */
export async function enviarNotas(ctx: Ctx, av: Reg, fmt: (n: number) => string) {
  const turma = ctx.porId(av.pai);
  if (!turma) return;
  const notas: Record<string, any> = av.c.notas ?? {};
  const alunos = ctx.filhos(turma.id, 'aluno').filter(a => notas[a.id] !== undefined && notas[a.id] !== '');
  if (!alunos.length) return Alert.alert(t('Nada a enviar'), t('Lance as notas antes de enviar.'));
  const g = await grupoDaTurma(turma.id);
  if (!g) return semGrupo(turma, ctx);
  let membros;
  try { membros = await membrosDe(g.id); } catch (e) { return erro(e); }
  const igual = (a: string, b: string) => { const x = norm(a).trim(), y = norm(b).trim(); return !!x && !!y && (x === y || x.startsWith(y + ' ') || y.startsWith(x + ' ')); };
  const pares = alunos.map(a => ({ a, ms: membros.filter(m => !m.admin && (igual(m.nome, String(a.c.nome ?? '')) || igual(m.nome, String(a.c.responsavel ?? '')))) }));
  const achados = pares.filter(p => p.ms.length), faltam = pares.filter(p => !p.ms.length).map(p => String(p.a.c.nome ?? '?'));
  const nomeAv = String(av.c.nome || t('Avaliação'));
  if (!achados.length) return Alert.alert(t('Ninguém encontrado no grupo'), t('As notas vão só para cada aluno. Para isso, o nome da pessoa no grupo precisa ser igual ao nome do aluno ou do responsável no cadastro.'));
  const msg = t('{n} aluno(s) recebem só a própria nota de "{a}".', { n: achados.length, a: nomeAv })
    + (faltam.length ? '\n\n' + t('Não estão no grupo (ou com outro nome): {l}', { l: faltam.join(', ') }) : '');
  Alert.alert(t('Enviar as notas'), msg, [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Enviar'), onPress: async () => {
      try {
        for (const { a, ms } of achados) {
          const texto = t('{aluno}: {n} de {max}', { aluno: String(a.c.nome ?? ''), n: fmt(num(notas[a.id])), max: num(av.c.max) || 10 });
          await enviar(g.id, 'aviso', t('Nota: {a}', { a: nomeAv }), { texto, emoji: '📝' }, ms.map(m => m.user_id));
        }
        Alert.alert(t('Enviado'), t('{n} aluno(s) receberam a nota.', { n: achados.length }));
      } catch (e) { erro(e); }
    } },
  ]);
}

// ─── Cobranças: por turma (todos de uma vez) e da aula extra ───
/** Uma cobrança para cada aluno indicado (sem repetir a mesma referência para o mesmo aluno). */
export function cobrarAlunos(ctx: Ctx, turma: Reg, ids: string[], p: { ref: string; valor: unknown; venc: string; tipo: string; valorDoAluno?: boolean }) {
  const ja = new Set(ctx.filhos(turma.id, 'cobranca').filter(c => c.c.ref === p.ref).map(c => c.c.aluno));
  const novas = ids.filter(id => !ja.has(id)).map(id => ctx.porId(id)).filter((a): a is Reg => !!a)
    .map(a => ({ a, v: p.valorDoAluno && temNum(a.c.valor) ? a.c.valor : p.valor })).filter(x => temNum(x.v))
    .map(x => novoReg('cobranca', turma.id, { aluno: x.a.id, ref: p.ref, venc: p.venc, valor: x.v, pago: false, tipo: p.tipo }));
  if (novas.length) salvarVarios(ctx.mod, novas);
  return novas.length;
}

/** Cobra a aula extra de quem estava (ou de todos, sem chamada no dia). */
export function cobrarAulaExtra(ctx: Ctx, turmaId: string | null | undefined, data: string) {
  const turma = ctx.porId(turmaId);
  if (!turma) return;
  if (!temNum(turma.c.valorExtra)) return Alert.alert(t('Falta o valor'), t('Coloque o valor da aula extra na turma (Editar).'));
  const ch = ctx.filhos(turma.id, 'chamada').find(c => c.c.data === data);
  const fora = new Set<string>([...(ch?.c.faltas ?? []), ...(ch?.c.outros ?? [])]);
  const ids = ctx.filhos(turma.id, 'aluno').filter(a => !fora.has(a.id)).map(a => a.id);
  const ref = t('Aula extra de {d}', { d: dataBr(data) });
  const venc = data > today() ? data : today();
  Alert.alert(t('Cobrar a aula extra'), t('{n} aluno(s) · {v} cada', { n: ids.length, v: din(turma.c.valorExtra, ctx.moeda) }) + (ch ? '' : '\n' + t('Sem chamada neste dia: cobra todos os alunos.')), [
    { text: t('Cancelar'), style: 'cancel' },
    { text: t('Gerar'), onPress: () => {
      const n = cobrarAlunos(ctx, turma, ids, { ref, valor: turma.c.valorExtra, venc, tipo: 'Aula extra' });
      Alert.alert(n ? t('Pronto') : t('Nada a gerar'), n ? t('{n} cobrança(s) criada(s) com vencimento em {d}.', { n, d: dataBr(venc) }) : t('As cobranças desta aula extra já existem.'));
    } },
  ]);
}
